import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, X, Briefcase, Building2, User, Clock, Tag,
  FileText, Users, Gavel, Home, Calculator, Scale, Plus,
  ChevronRight, Loader2, Sparkles, FolderOpen, Check
} from 'lucide-react';

/**
 * Case Selector Modal - World Class Design
 * 
 * Features:
 * - Real-time search with keyboard navigation
 * - AI-suggested matches at top
 * - Case type badges with icons
 * - Client info display
 * - Recent cases section
 * - Quick create new case option
 * - ChatGPT meets Linear design
 */
const CaseSelectorModal = ({
  isOpen,
  onClose,
  cases = [],
  onSelectCase,
  actionInProgress,
  suggestedCaseId,
  onCreateNew
}) => {
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  // Focus search on open
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
    if (isOpen) {
      setSearch('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Case type config
  const caseTypeConfig = {
    'CONTRACT': { label: 'Kontrakt', icon: FileText, color: 'bg-violet-100 text-violet-700' },
    'CORPORATE': { label: 'Selskap', icon: Building2, color: 'bg-purple-100 text-purple-700' },
    'EMPLOYMENT': { label: 'Arbeid', icon: Users, color: 'bg-green-100 text-green-700' },
    'DISPUTE': { label: 'Tvist', icon: Gavel, color: 'bg-red-100 text-red-700' },
    'REAL_ESTATE': { label: 'Eiendom', icon: Home, color: 'bg-amber-100 text-amber-700' },
    'TAX': { label: 'Skatt', icon: Calculator, color: 'bg-gray-100 text-gray-700' },
    'IP': { label: 'IP', icon: Scale, color: 'bg-pink-100 text-pink-700' },
    'OTHER': { label: 'Annet', icon: Briefcase, color: 'bg-gray-100 text-gray-700' }
  };

  // Filter and sort cases
  const filteredCases = useMemo(() => {
    let result = [...cases];
    
    // Apply search filter
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      result = result.filter(c => 
        (c.title || c.topic || '').toLowerCase().includes(searchLower) ||
        (c.client_name || '').toLowerCase().includes(searchLower) ||
        (c.case_number || '').toLowerCase().includes(searchLower)
      );
    }
    
    // Sort: suggested first, then by date
    result.sort((a, b) => {
      if (a.id === suggestedCaseId) return -1;
      if (b.id === suggestedCaseId) return 1;
      return new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime();
    });
    
    return result;
  }, [cases, search, suggestedCaseId]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredCases.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && filteredCases[selectedIndex]) {
        e.preventDefault();
        onSelectCase(filteredCases[selectedIndex].id);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, selectedIndex, filteredCases, onSelectCase]);

  // Scroll selected into view
  useEffect(() => {
    if (listRef.current && filteredCases.length > 0) {
      const selectedEl = listRef.current.children[selectedIndex];
      selectedEl?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, filteredCases.length]);

  // Format relative time
  const formatRelativeTime = (dateStr: string | undefined) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'I dag';
    if (diffDays === 1) return 'I går';
    if (diffDays < 7) return `${diffDays} dager siden`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} uker siden`;
    return date.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/40"
          onClick={onClose}
        />
        
        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, y: 8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.15 }}
          onClick={(e) => e.stopPropagation()}
          className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-violet-600 flex items-center justify-center">
                  <FolderOpen className="w-3.5 h-3.5 text-white" />
                </div>
                <h2 className="text-[15px] font-semibold text-gray-900">Knytt til sak</h2>
              </div>
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -trangray-y-1/2" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setSelectedIndex(0); }}
                placeholder="Søk etter sak, klient eller saksnummer..."
                className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:border-violet-500 focus:ring-1 focus:ring-violet-500 outline-none"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -trangray-y-1/2 p-0.5 hover:bg-gray-100 rounded"
                >
                  <X className="w-3 h-3 text-gray-400" />
                </button>
              )}
            </div>
            
            {/* Keyboard hints */}
            <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-400">
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-500">↑↓</kbd>
                naviger
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-500">Enter</kbd>
                velg
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-500">Esc</kbd>
                lukk
              </span>
            </div>
          </div>
          
          {/* Case list */}
          <div ref={listRef} className="max-h-[50vh] overflow-y-auto">
            {filteredCases.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <Search className="w-5 h-5 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500 mb-1">Ingen saker funnet</p>
                <p className="text-xs text-gray-400">Prøv et annet søkeord</p>
              </div>
            ) : (
              filteredCases.map((caseItem, index) => {
                const typeConfig = caseTypeConfig[caseItem.case_type] || caseTypeConfig['OTHER'];
                const TypeIcon = typeConfig.icon;
                const isSuggested = caseItem.id === suggestedCaseId;
                const isSelected = index === selectedIndex;
                
                return (
                  <button
                    key={caseItem.id}
                    onClick={() => onSelectCase(caseItem.id)}
                    disabled={actionInProgress === 'link'}
                    className={`w-full px-4 py-3 text-left transition-colors border-b border-gray-50 last:border-b-0 disabled:opacity-50 ${
                      isSelected ? 'bg-violet-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Case type icon */}
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${typeConfig.color}`}>
                        <TypeIcon className="w-4 h-4" />
                      </div>
                      
                      {/* Case info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            {/* Title + suggested badge */}
                            <div className="flex items-center gap-2">
                              <h4 className="text-sm font-medium text-gray-900 truncate">
                                {(caseItem.title && caseItem.title !== 'General Legal Matter') 
                                  ? caseItem.title 
                                  : (caseItem.topic || caseItem.title || 'Untitled Case')}
                              </h4>
                              {isSuggested && (
                                <span className="flex items-center gap-1 px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded text-[10px] font-semibold flex-shrink-0">
                                  <Sparkles className="w-2.5 h-2.5" />
                                  Foreslått
                                </span>
                              )}
                            </div>
                            
                            {/* Client */}
                            {caseItem.client_name && (
                              <div className="flex items-center gap-1.5 mt-0.5">
                                {caseItem.client_org ? (
                                  <Building2 className="w-3 h-3 text-gray-400" />
                                ) : (
                                  <User className="w-3 h-3 text-gray-400" />
                                )}
                                <span className="text-xs text-gray-500 truncate">
                                  {caseItem.client_name}
                                  {caseItem.client_org && caseItem.client_org !== caseItem.client_name && (
                                    <span className="text-gray-400"> • {caseItem.client_org}</span>
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* Right side: status + time */}
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            {/* Case number */}
                            {caseItem.case_number && (
                              <span className="text-[10px] text-gray-400 font-mono">
                                {caseItem.case_number}
                              </span>
                            )}
                            {/* Time */}
                            <span className="text-[10px] text-gray-400 flex items-center gap-1">
                              <Clock className="w-2.5 h-2.5" />
                              {formatRelativeTime(caseItem.updated_at || caseItem.created_at)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Tags */}
                        {caseItem.tags?.length > 0 && (
                          <div className="flex items-center gap-1 mt-1.5">
                            {caseItem.tags.slice(0, 3).map((tag, i) => (
                              <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px]">
                                {tag}
                              </span>
                            ))}
                            {caseItem.tags.length > 3 && (
                              <span className="text-[10px] text-gray-400">+{caseItem.tags.length - 3}</span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Selection indicator */}
                      {isSelected && (
                        <div className="w-5 h-5 rounded-full bg-violet-600 flex items-center justify-center flex-shrink-0 mt-2">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
          
          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">
                {filteredCases.length} sak{filteredCases.length !== 1 ? 'er' : ''}
              </span>
              {onCreateNew && (
                <button
                  onClick={onCreateNew}
                  className="flex items-center gap-1.5 text-xs text-violet-600 hover:text-violet-700 font-medium"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Opprett ny sak i stedet
                </button>
              )}
            </div>
          </div>
          
          {/* Loading overlay */}
          {actionInProgress === 'link' && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                Knytter til sak...
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CaseSelectorModal;
